import React, { useEffect, useMemo, useState } from 'react';
import { Box, Slider, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import FlagIcon from '@mui/icons-material/Flag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { PsfColors } from '../theme';
import { AdjustMode, AmenityPremium, amenityPremiumAmount, amenityPremiumApplies } from '../lib/adjustments';

/** Distance + base PSF for one project, as fed to the Level 4 cards. */
export interface AmenityProjectPoint {
  distance: number | null;
  basePsf: number | null;
}

export interface AmenityPremiumStats {
  near: number;
  boosted: number;
  noData: number;
  /** Mean $psf added across boosted projects. */
  avgBoost: number;
}

// The chart and slider share one 0 → SCALE_MAX axis; farther projects pile
// into the last bar.
const SCALE_MAX = 2000;
const BIN_COUNT = 40;
const BIN_WIDTH = SCALE_MAX / BIN_COUNT;

const pop = keyframes`
  0% { transform: scale(1); }
  35% { transform: scale(1.28); }
  100% { transform: scale(1); }
`;

const fmtMeters = (meters: number) => (meters >= 1000 ? `${+(meters / 1000).toFixed(2)} km` : `${Math.round(meters)} m`);

export function amenityPremiumStats(premium: AmenityPremium, points: AmenityProjectPoint[]): AmenityPremiumStats {
  let near = 0;
  let boosted = 0;
  let noData = 0;
  let boostTotal = 0;
  points.forEach(({ distance, basePsf }) => {
    if (distance == null || !Number.isFinite(distance)) { noData += 1; return; }
    if (!amenityPremiumApplies(premium, distance)) { near += 1; return; }
    boosted += 1;
    if (basePsf != null) boostTotal += amenityPremiumAmount(premium, distance, basePsf);
  });
  return { near, boosted, noData, avgBoost: boosted ? Math.round(boostTotal / boosted) : 0 };
}

/** Re-keys its child whenever `value` changes so the pop animation replays. */
function Pop({ value, children }: { value: string | number; children: React.ReactNode }) {
  return (
    <Box key={value} component="span" sx={{ display: 'inline-block', animation: `${pop} 320ms ease-out` }}>
      {children}
    </Box>
  );
}

function DraftNumber({ value, onCommit, width, sx }: { value: number; onCommit: (value: number) => void; width: number; sx?: object }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        const next = Number(event.target.value);
        if (event.target.value !== '' && Number.isFinite(next)) onCommit(next);
      }}
      onBlur={() => { if (draft === '' || !Number.isFinite(Number(draft))) setDraft(String(value)); }}
      onFocus={(event) => event.currentTarget.select()}
      sx={{ width, ...sx }}
    />
  );
}

const PREMIUM_PRESETS: Array<{ label: string; value: number; mode: AdjustMode }> = [
  { label: '+3%', value: 3, mode: 'pct' },
  { label: '+5%', value: 5, mode: 'pct' },
  { label: '+8%', value: 8, mode: 'pct' },
  { label: '+$50', value: 50, mode: 'psf' },
  { label: '+$100', value: 100, mode: 'psf' },
];

export default function AmenityPremiumCard({
  title, icon, accent, premium, onChange, points, distancePresets, c,
}: {
  title: string;
  icon: React.ReactNode;
  /** 6-digit hex — alpha tints are appended. */
  accent: string;
  premium: AmenityPremium;
  onChange: (next: AmenityPremium) => void;
  points: AmenityProjectPoint[];
  distancePresets: number[];
  c: PsfColors;
}) {
  const on = premium.enabled;
  const preferred = premium.preferredDistanceMeters;
  const update = (partial: Partial<AmenityPremium>) => onChange({ ...premium, ...partial });

  const bins = useMemo(() => {
    const counts = new Array<number>(BIN_COUNT).fill(0);
    points.forEach(({ distance }) => {
      if (distance == null || !Number.isFinite(distance) || distance < 0) return;
      counts[Math.min(BIN_COUNT - 1, Math.floor(distance / BIN_WIDTH))] += 1;
    });
    return counts;
  }, [points]);
  const peak = Math.max(1, ...bins);
  const stats = useMemo(() => amenityPremiumStats(premium, points), [premium, points]);
  const premiumLabel = premium.mode === 'pct' ? `+${premium.value}%` : `+$${premium.value}`;
  const flagLeft = `${(Math.min(preferred, SCALE_MAX) / SCALE_MAX) * 100}%`;
  const green = c.success;

  return (
    <Box sx={{
      mt: 1.25, p: 1.1, borderRadius: 2, position: 'relative', overflow: 'hidden',
      border: `1.5px solid ${on ? `${accent}aa` : c.border}`,
      background: on ? `linear-gradient(165deg, ${accent}1c, transparent 55%)` : 'transparent',
      boxShadow: on && premium.value ? `0 6px 20px -12px ${accent}` : 'none',
      transition: 'all 200ms',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
          color: '#fff', background: on ? `linear-gradient(135deg, ${accent}, ${accent}aa)` : c.textMuted,
          boxShadow: on ? `0 3px 10px -3px ${accent}` : 'none', '& svg': { fontSize: 16 },
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: c.text, flex: 1 }}>{title}</Typography>
        <Switch
          size="small"
          checked={on}
          onChange={(event) => update({ enabled: event.target.checked })}
          inputProps={{ 'aria-label': `${title} premium on` }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: `${accent} !important` },
          }}
        />
      </Box>

      <Box sx={{ opacity: on ? 1 : 0.45, pointerEvents: on ? 'auto' : 'none', transition: 'opacity 200ms' }}>
        {/* Step 1 — preferred distance */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: accent, letterSpacing: 0.5 }}>STEP 1</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: c.textMuted }}>Buyer wants it within</Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: green, lineHeight: 1 }}>
            <Pop value={preferred}>{fmtMeters(preferred)}</Pop>
          </Typography>
        </Box>

        {/* Distance chart: green = within preferred, accent = premium zone */}
        <Box sx={{ position: 'relative', mt: 1.75, height: 54 }}>
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: '1px',
          }}>
            {bins.map((count, index) => {
              const binStart = index * BIN_WIDTH;
              const inside = binStart < preferred;
              return (
                <Tooltip
                  key={index}
                  title={`${index === BIN_COUNT - 1 ? `${fmtMeters(binStart)}+` : `${fmtMeters(binStart)}–${fmtMeters(binStart + BIN_WIDTH)}`}: ${count} project${count === 1 ? '' : 's'}`}
                  disableInteractive
                >
                  <Box sx={{
                    flex: 1, minWidth: 0,
                    height: count ? `${Math.max(8, (count / peak) * 100)}%` : '3px',
                    borderRadius: '2px 2px 0 0',
                    bgcolor: count ? (inside ? green : accent) : c.border,
                    opacity: count ? (inside ? 0.85 : 1) : 0.5,
                    boxShadow: count && !inside && premium.value ? `0 0 6px ${accent}99` : 'none',
                    transition: 'background-color 160ms, box-shadow 160ms',
                  }} />
                </Tooltip>
              );
            })}
          </Box>
          {/* Preferred-distance flag */}
          <Box sx={{
            position: 'absolute', top: -6, bottom: 0, left: flagLeft, width: 0,
            borderLeft: `2px dashed ${c.text}`, pointerEvents: 'none', transition: 'left 80ms',
          }}>
            <FlagIcon sx={{ position: 'absolute', top: -8, left: -3, fontSize: 15, color: green }} />
          </Box>
        </Box>
        <Slider
          value={Math.min(preferred, SCALE_MAX)}
          min={0}
          max={SCALE_MAX}
          step={50}
          onChange={(_, value) => update({ preferredDistanceMeters: Math.max(50, value as number) })}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => fmtMeters(Math.max(50, value))}
          aria-label={`${title} preferred distance`}
          sx={{
            color: green, py: 0.75, mt: -0.25,
            '& .MuiSlider-rail': { opacity: 1, height: 4, background: `linear-gradient(90deg, ${green}55, ${accent}88)` },
            '& .MuiSlider-track': { height: 4, border: 'none' },
            '& .MuiSlider-thumb': {
              width: 16, height: 16, bgcolor: '#fff', border: `3px solid ${green}`,
              '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${green}33` },
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.75 }}>
          <Typography sx={{ fontSize: '0.54rem', color: c.textMuted }}>0</Typography>
          <Typography sx={{ fontSize: '0.54rem', color: c.textMuted }}>1 km</Typography>
          <Typography sx={{ fontSize: '0.54rem', color: c.textMuted }}>2 km+</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
          {distancePresets.map((meters) => {
            const selected = preferred === meters;
            return (
              <Box
                key={meters}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => update({ preferredDistanceMeters: meters })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); update({ preferredDistanceMeters: meters }); }
                }}
                sx={{
                  px: 0.75, py: 0.15, borderRadius: 5, cursor: 'pointer', userSelect: 'none',
                  fontSize: '0.6rem', fontWeight: 800,
                  border: `1px solid ${selected ? green : c.border}`,
                  bgcolor: selected ? green : 'transparent',
                  color: selected ? '#fff' : c.textMuted,
                  '&:hover': { borderColor: green },
                }}
              >
                ≤ {fmtMeters(meters)}
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, ml: 'auto' }}>
            <DraftNumber
              value={preferred}
              width={64}
              onCommit={(value) => update({ preferredDistanceMeters: Math.max(0, value) })}
              sx={{ '& input': { color: c.text, fontSize: '0.68rem', py: 0.3, px: 0.6 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border } }}
            />
            <Typography sx={{ fontSize: '0.6rem', color: c.textMuted }}>m</Typography>
          </Box>
        </Box>

        {/* Step 2 — premium */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1.1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: accent, letterSpacing: 0.5 }}>STEP 2</Typography>
          <Typography sx={{ fontSize: '0.66rem', color: c.textMuted }}>Premium for projects farther than {fmtMeters(preferred)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: accent }}>+</Typography>
          <DraftNumber
            value={premium.value}
            width={66}
            onCommit={(value) => update({ value })}
            sx={{
              '& input': { color: c.text, fontSize: '0.8rem', fontWeight: 800, py: 0.4, px: 0.75 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: premium.value ? accent : c.border, borderWidth: premium.value ? 2 : 1 },
            }}
          />
          <ToggleButtonGroup
            value={premium.mode}
            exclusive
            size="small"
            onChange={(_, mode: AdjustMode | null) => { if (mode) update({ mode }); }}
            sx={{
              '& .MuiToggleButton-root': { py: 0.2, px: 0.8, fontSize: '0.66rem', fontWeight: 800, color: c.textMuted, borderColor: c.border },
              '& .Mui-selected': { bgcolor: `${accent} !important`, color: '#fff !important' },
            }}
          >
            <ToggleButton value="pct">%</ToggleButton>
            <ToggleButton value="psf">$ psf</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.6 }}>
          {PREMIUM_PRESETS.map((preset) => {
            const selected = premium.value === preset.value && premium.mode === preset.mode;
            return (
              <Box
                key={preset.label}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => update({ value: preset.value, mode: preset.mode })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); update({ value: preset.value, mode: preset.mode }); }
                }}
                sx={{
                  px: 0.8, py: 0.2, borderRadius: 5, cursor: 'pointer', userSelect: 'none',
                  fontSize: '0.62rem', fontWeight: 800,
                  border: `1px solid ${selected ? accent : c.border}`,
                  bgcolor: selected ? accent : 'transparent',
                  color: selected ? '#fff' : c.textMuted,
                  transition: 'transform 120ms',
                  '&:hover': { borderColor: accent, transform: 'translateY(-1px)' },
                  '&:active': { transform: 'scale(0.94)' },
                }}
              >
                {preset.label}
              </Box>
            );
          })}
        </Box>

        {/* Scoreboard */}
        {!on ? (
          <Typography sx={{ fontSize: '0.64rem', color: c.textMuted, mt: 1.1, textAlign: 'center' }}>
            Premium off — no project is adjusted for {title}.
          </Typography>
        ) : (
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.5, mt: 1.1,
        }}>
          {[
            { key: 'near', icon: <VerifiedIcon />, color: green, value: stats.near.toLocaleString(), label: 'within' },
            { key: 'boosted', icon: <TrendingUpIcon />, color: accent, value: stats.boosted.toLocaleString(), label: `get ${premiumLabel}` },
            { key: 'avg', icon: null, color: accent, value: `+$${stats.avgBoost.toLocaleString()}`, label: 'avg psf boost' },
          ].map((tile) => (
            <Box key={tile.key} sx={{
              p: 0.6, borderRadius: 1.25, textAlign: 'center', minWidth: 0,
              bgcolor: `${tile.color}14`, border: `1px solid ${tile.color}44`,
            }}>
              <Typography sx={{
                fontSize: '0.9rem', fontWeight: 900, color: tile.color, lineHeight: 1.1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, '& svg': { fontSize: 13 },
              }}>
                {tile.icon}
                <Pop value={tile.value}>{tile.value}</Pop>
              </Typography>
              <Typography sx={{ fontSize: '0.56rem', color: c.textMuted, lineHeight: 1.2 }} noWrap>{tile.label}</Typography>
            </Box>
          ))}
        </Box>
        )}
        {on && stats.noData > 0 && (
          <Typography sx={{ fontSize: '0.56rem', color: c.textMuted, mt: 0.5 }}>
            {stats.noData.toLocaleString()} project{stats.noData === 1 ? ' has' : 's have'} no distance data — no premium applied.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
